// features/inventory/pages/CountsListPage.tsx
/**
 * Stock Counts List Page — View, filter, and manage count sessions.
 *
 * Route: /inventory/counts
 *
 * Features:
 * - Card and table view toggle
 * - Filter by status (all, active, completed, cancelled)
 * - Search by branch name or count type
 * - Sort by date or item count
 * - Quick stats: total sessions, active, completed, variances
 * - Start new count modal with type selection (full/cycle) and branch picker
 * - Navigate to live counting or view completed report
 * - Progress bar for active counts
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { getCountSessions, createCountSession, CountSession } from '../services/inventoryApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft,
    ClipboardList,
    Plus,
    Search,
    X,
    RefreshCw,
    Calculator,
    Package,
    Clock,
    CheckCircle,
    AlertTriangle,
    TrendingUp,
    Calendar,
    Store,
    Play,
    Eye,
    BarChart3,
    LayoutGrid,
    List,
    Filter,
    Layers,
    Activity,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

type ViewMode = 'card' | 'table';
type CountFilter = 'all' | 'in_progress' | 'completed' | 'cancelled';
type SortBy = 'newest' | 'oldest' | 'most_items';

// ============================================================
// SUB-COMPONENTS
// ============================================================

function QuickStat({
    label,
    value,
    icon: Icon,
    color,
    pulse,
}: {
    label: string;
    value: string | number;
    icon: React.ElementType;
    color: string;
    pulse?: boolean;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all"
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        {label}
                    </p>
                    <p
                        className={clsx(
                            'text-2xl font-bold mt-1',
                            pulse ? 'text-blue-600' : 'text-slate-900'
                        )}
                    >
                        {typeof value === 'number'
                            ? value.toLocaleString()
                            : value}
                    </p>
                </div>
                <div
                    className={clsx(
                        'p-2.5 rounded-xl bg-gradient-to-br shadow-lg',
                        color
                    )}
                >
                    <Icon
                        className={clsx(
                            'w-5 h-5 text-white',
                            pulse && 'animate-pulse'
                        )}
                    />
                </div>
            </div>
        </motion.div>
    );
}

function CountCardView({
    counts,
    getStatusBadge,
    formatDate,
    getBranchName,
    navigate,
}: {
    counts: CountSession[];
    getStatusBadge: (status: string) => React.ReactNode;
    formatDate: (dateStr: string | undefined) => string;
    getBranchName: (id: string) => string;
    navigate: ReturnType<typeof useNavigate>;
}) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {counts.map((count) => {
                const isActive =
                    count.status === 'draft' ||
                    count.status === 'in_progress';
                const isCompleted = count.status === 'completed';
                const progressPercent =
                    count.totalItems > 0
                        ? Math.round(
                            (count.countedItems / count.totalItems) *
                            100
                        )
                        : 0;

                return (
                    <motion.div
                        key={count.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -4, scale: 1.01 }}
                        onClick={() =>
                            navigate(`/inventory/counts/${count.id}`)
                        }
                        className={clsx(
                            'bg-white rounded-2xl border-2 p-5 transition-all cursor-pointer hover:shadow-xl',
                            isActive
                                ? 'border-blue-200 hover:border-blue-300'
                                : isCompleted
                                    ? 'border-emerald-200 hover:border-emerald-300'
                                    : 'border-slate-200 hover:border-slate-300'
                        )}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                {getStatusBadge(count.status)}
                                <Badge variant="info" size="sm">
                                    {count.countType?.toUpperCase()}
                                </Badge>
                            </div>
                            <motion.button
                                whileHover={{
                                    scale: 1.15,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                        `/inventory/counts/${count.id}`
                                    );
                                }}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                type="button"
                            >
                                {isActive ? (
                                    <Play className="w-4 h-4" />
                                ) : (
                                    <Eye className="w-4 h-4" />
                                )}
                            </motion.button>
                        </div>
                        <div className="mb-3">
                            <div className="flex items-center gap-2 text-sm">
                                <Store className="w-3.5 h-3.5 text-slate-400" />
                                <span className="font-medium text-slate-700">
                                    {count.branchName ||
                                        getBranchName(count.branchId)}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                                <Calendar className="w-3 h-3" />{' '}
                                {formatDate(count.createdAt)}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="text-center p-2 bg-slate-50 rounded-lg">
                                <p className="text-[10px] text-slate-400 uppercase">
                                    Total
                                </p>
                                <p className="text-lg font-bold text-slate-700">
                                    {count.totalItems}
                                </p>
                            </div>
                            <div className="text-center p-2 bg-emerald-50 rounded-lg">
                                <p className="text-[10px] text-emerald-600 uppercase">
                                    Counted
                                </p>
                                <p className="text-lg font-bold text-emerald-700">
                                    {count.countedItems}
                                </p>
                            </div>
                            <div className="text-center p-2 bg-rose-50 rounded-lg">
                                <p className="text-[10px] text-rose-600 uppercase">
                                    Variances
                                </p>
                                <p className="text-lg font-bold text-rose-700">
                                    {count.varianceItems}
                                </p>
                            </div>
                        </div>
                        {isActive && (
                            <div>
                                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                    <motion.div
                                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{
                                            width: `${progressPercent}%`,
                                        }}
                                        transition={{
                                            duration: 0.5,
                                        }}
                                    />
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1 text-right">
                                    {progressPercent}% complete
                                </p>
                            </div>
                        )}
                        {isCompleted && (
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-2 pt-2 border-t border-slate-100">
                                <CheckCircle className="w-3 h-3 text-emerald-500" />{' '}
                                Completed{' '}
                                {count.completedAt
                                    ? formatDate(count.completedAt)
                                    : ''}
                            </div>
                        )}
                        {count.notes && (
                            <p className="text-xs text-slate-400 mt-2 truncate italic">
                                "{count.notes}"
                            </p>
                        )}
                    </motion.div>
                );
            })}
        </div>
    );
}

function CountTableView({
    counts,
    getStatusBadge,
    formatDate,
    getBranchName,
    navigate,
}: {
    counts: CountSession[];
    getStatusBadge: (status: string) => React.ReactNode;
    formatDate: (dateStr: string | undefined) => string;
    getBranchName: (id: string) => string;
    navigate: ReturnType<typeof useNavigate>;
}) {
    return (
        <Card className="overflow-hidden shadow-sm border border-slate-200">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                    <thead>
                        <tr className="bg-slate-50/50">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                Branch
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                Type
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                Status
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                Total
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                Counted
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                Variances
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                                Progress
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-16">
                                View
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {counts.map((count) => {
                            const progressPercent =
                                count.totalItems > 0
                                    ? Math.round(
                                        (count.countedItems /
                                            count.totalItems) *
                                        100
                                    )
                                    : 0;
                            return (
                                <motion.tr
                                    key={count.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.01 }}
                                    onClick={() =>
                                        navigate(
                                            `/inventory/counts/${count.id}`
                                        )
                                    }
                                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                                >
                                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                                        {formatDate(count.createdAt)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-700">
                                        {count.branchName ||
                                            getBranchName(
                                                count.branchId
                                            )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge variant="info" size="sm">
                                            {count.countType}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {getStatusBadge(count.status)}
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm font-medium">
                                        {count.totalItems}
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm font-medium text-emerald-600">
                                        {count.countedItems}
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm font-medium text-rose-600">
                                        {count.varianceItems}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                                <div
                                                    className={clsx(
                                                        'h-1.5 rounded-full',
                                                        count.status ===
                                                            'completed'
                                                            ? 'bg-emerald-500'
                                                            : 'bg-blue-500'
                                                    )}
                                                    style={{
                                                        width: `${progressPercent}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="text-xs text-slate-400 w-8 text-right">
                                                {progressPercent}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <motion.div
                                            whileHover={{
                                                scale: 1.2,
                                            }}
                                            className="inline-flex p-1 rounded-lg text-slate-300 group-hover:text-emerald-500 group-hover:bg-emerald-50 transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </motion.div>
                                    </td>
                                </motion.tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function NewCountModal({
    isOpen,
    onClose,
    branchesList,
    isSingleStore,
    onCreateCount,
    isPending,
}: {
    isOpen: boolean;
    onClose: () => void;
    branchesList: Array<{ id: string; name: string; code?: string }>;
    isSingleStore: boolean;
    onCreateCount: (type: 'full' | 'cycle', branchId: string, notes: string) => void;
    isPending: boolean;
}) {
    const [countType, setCountType] = useState<'full' | 'cycle'>('full');
    const [branchId, setBranchId] = useState<string>(NIL_UUID);
    const [notes, setNotes] = useState('');

    const handleSubmit = () => {
        onCreateCount(countType, branchId, notes);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-white/20">
                                        <ClipboardList className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold">
                                            Start New Count
                                        </h2>
                                        <p className="text-emerald-100 text-xs">
                                            {isSingleStore
                                                ? 'Warehouse inventory count'
                                                : 'Select type and branch'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 rounded-xl bg-white/20 hover:bg-white/30"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6 space-y-5">
                            {/* Branch Selection */}
                            {!isSingleStore &&
                                branchesList.length > 0 && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            <Store className="w-3.5 h-3.5 inline mr-1" />{' '}
                                            Branch *
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {branchesList.map(
                                                (b: any) => (
                                                    <button
                                                        key={b.id}
                                                        onClick={() =>
                                                            setBranchId(
                                                                b.id
                                                            )
                                                        }
                                                        className={clsx(
                                                            'p-3 rounded-xl border-2 text-left transition-all text-sm',
                                                            branchId ===
                                                                b.id
                                                                ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20'
                                                                : 'border-slate-200 hover:border-emerald-300'
                                                        )}
                                                        type="button"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Store
                                                                className={clsx(
                                                                    'w-4 h-4',
                                                                    branchId ===
                                                                        b.id
                                                                        ? 'text-emerald-600'
                                                                        : 'text-slate-400'
                                                                )}
                                                            />
                                                            <span className="font-medium truncate">
                                                                {
                                                                    b.name
                                                                }
                                                            </span>
                                                        </div>
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}

                            {/* Count Type */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Count Type *
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() =>
                                            setCountType('full')
                                        }
                                        className={clsx(
                                            'p-4 rounded-xl border-2 text-center transition-all',
                                            countType === 'full'
                                                ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20'
                                                : 'border-slate-200 hover:border-emerald-300'
                                        )}
                                        type="button"
                                    >
                                        <div
                                            className={clsx(
                                                'w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2',
                                                countType === 'full'
                                                    ? 'bg-emerald-100'
                                                    : 'bg-slate-100'
                                            )}
                                        >
                                            <Calculator
                                                className={clsx(
                                                    'w-6 h-6',
                                                    countType ===
                                                        'full'
                                                        ? 'text-emerald-600'
                                                        : 'text-slate-400'
                                                )}
                                            />
                                        </div>
                                        <h4 className="font-bold text-sm">
                                            Full Count
                                        </h4>
                                        <p className="text-xs text-slate-400 mt-1">
                                            Count every product in
                                            stock
                                        </p>
                                    </button>
                                    <button
                                        onClick={() =>
                                            setCountType('cycle')
                                        }
                                        className={clsx(
                                            'p-4 rounded-xl border-2 text-center transition-all',
                                            countType === 'cycle'
                                                ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20'
                                                : 'border-slate-200 hover:border-emerald-300'
                                        )}
                                        type="button"
                                    >
                                        <div
                                            className={clsx(
                                                'w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2',
                                                countType ===
                                                    'cycle'
                                                    ? 'bg-emerald-100'
                                                    : 'bg-slate-100'
                                            )}
                                        >
                                            <Package
                                                className={clsx(
                                                    'w-6 h-6',
                                                    countType ===
                                                        'cycle'
                                                        ? 'text-emerald-600'
                                                        : 'text-slate-400'
                                                )}
                                            />
                                        </div>
                                        <h4 className="font-bold text-sm">
                                            Cycle Count
                                        </h4>
                                        <p className="text-xs text-slate-400 mt-1">
                                            Count selected products
                                            only
                                        </p>
                                    </button>
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={(e) =>
                                        setNotes(e.target.value)
                                    }
                                    rows={2}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                    placeholder="e.g., End of month physical count, Spot check aisle 3..."
                                />
                            </div>

                            {/* Info */}
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2">
                                <Clock className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-xs font-semibold text-blue-700">
                                        What happens next?
                                    </p>
                                    <p className="text-xs text-blue-600 mt-0.5">
                                        {countType === 'full'
                                            ? "All products with stock will be added to the count. You can start counting immediately."
                                            : "You'll select specific products to count. Useful for high-value or fast-moving items."}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-3 rounded-b-3xl">
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm"
                                type="button"
                            >
                                Cancel
                            </button>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleSubmit}
                                disabled={
                                    isPending ||
                                    (!isSingleStore && !branchId)
                                }
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold text-sm shadow-lg disabled:opacity-50"
                                type="button"
                            >
                                {isPending ? (
                                    <Spinner
                                        size="xs"
                                        variant="white"
                                    />
                                ) : (
                                    <Play className="w-4 h-4" />
                                )}{' '}
                                Start Count
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function CountsListPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [viewMode, setViewMode] = useState<ViewMode>('card');
    const [countFilter, setCountFilter] = useState<CountFilter>('all');
    const [sortBy, setSortBy] = useState<SortBy>('newest');
    const [searchQuery, setSearchQuery] = useState('');
    const [showNewCountModal, setShowNewCountModal] = useState(false);

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-counts', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const {
        data: counts,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: [
            'inventory-counts',
            user?.tenantId,
            countFilter,
        ],
        queryFn: () =>
            getCountSessions(user!.tenantId, {
                limit: 50,
                status:
                    countFilter === 'all'
                        ? undefined
                        : countFilter,
            }),
        enabled: !!user?.tenantId,
        refetchInterval: 15_000,
    });

    // Mutations
    const startCountMutation = useMutation({
        mutationFn: ({
            countType,
            branchId,
            notes,
        }: {
            countType: 'full' | 'cycle';
            branchId: string;
            notes: string;
        }) =>
            createCountSession(user!.tenantId, {
                branchId,
                countType,
                notes: notes || undefined,
            }),
        onSuccess: (count) => {
            addToast({
                type: 'success',
                title: 'Count started',
                message: `${count.totalItems} products loaded for counting.`,
            });
            setShowNewCountModal(false);
            queryClient.invalidateQueries({
                queryKey: ['inventory-counts'],
            });
            queryClient.invalidateQueries({
                queryKey: ['counts-dash'],
            });
            navigate(`/inventory/counts/${count.id}`);
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to start count',
                message:
                    err?.response?.data?.error?.message ||
                    err?.message,
            });
        },
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        code?: string;
    }>;
    const countsList = (counts || []) as CountSession[];
    const isSingleStore = branchesList.length === 0;

    // Filtered and sorted counts
    const filteredCounts = useMemo(() => {
        let result = countsList;

        // Search filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(
                (c) =>
                    (c.branchName || '')
                        .toLowerCase()
                        .includes(q) ||
                    c.countType.toLowerCase().includes(q) ||
                    (c.notes || '').toLowerCase().includes(q)
            );
        }

        // Sort
        switch (sortBy) {
            case 'oldest':
                result = [...result].sort(
                    (a, b) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                );
                break;
            case 'most_items':
                result = [...result].sort(
                    (a, b) => b.totalItems - a.totalItems
                );
                break;
            default:
                result = [...result].sort(
                    (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                );
                break;
        }

        return result;
    }, [countsList, searchQuery, sortBy]);

    // Stats
    const stats = useMemo(() => {
        const total = countsList.length;
        const inProgress = countsList.filter(
            (c) =>
                c.status === 'draft' ||
                c.status === 'in_progress'
        ).length;
        const completed = countsList.filter(
            (c) => c.status === 'completed'
        ).length;
        const cancelled = countsList.filter(
            (c) => c.status === 'cancelled'
        ).length;
        const totalVariances = countsList.reduce(
            (sum, c) => sum + (c.varianceItems || 0),
            0
        );
        const avgAccuracy =
            completed > 0
                ? Math.round(
                    countsList
                        .filter(
                            (c) =>
                                c.status === 'completed' &&
                                c.totalItems > 0
                        )
                        .reduce(
                            (sum, c) =>
                                sum +
                                ((c.totalItems -
                                    c.varianceItems) /
                                    c.totalItems) *
                                100,
                            0
                        ) / completed
                )
                : 0;
        return {
            total,
            inProgress,
            completed,
            cancelled,
            totalVariances,
            avgAccuracy,
        };
    }, [countsList]);

    // Handlers
    const getBranchName = useCallback(
        (id: string) => {
            if (id === NIL_UUID || !id) return 'Warehouse';
            return (
                branchesList.find((b: any) => b.id === id)
                    ?.name || 'Warehouse'
            );
        },
        [branchesList]
    );

    const formatDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'draft':
                return (
                    <Badge variant="neutral" size="sm">
                        Draft
                    </Badge>
                );
            case 'in_progress':
                return (
                    <Badge variant="info" size="sm">
                        <span className="animate-pulse inline-block mr-1">
                            ●
                        </span>
                        In Progress
                    </Badge>
                );
            case 'completed':
                return (
                    <Badge variant="success" size="sm">
                        Completed
                    </Badge>
                );
            case 'cancelled':
                return (
                    <Badge variant="danger" size="sm">
                        Cancelled
                    </Badge>
                );
            default:
                return (
                    <Badge variant="neutral" size="sm">
                        {status}
                    </Badge>
                );
        }
    };

    const handleCreateCount = useCallback(
        (
            countType: 'full' | 'cycle',
            branchId: string,
            notes: string
        ) => {
            startCountMutation.mutate({
                countType,
                branchId,
                notes,
            });
        },
        [startCountMutation]
    );

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <motion.div
                                whileHover={{ rotate: -5 }}
                                className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25"
                            >
                                <ClipboardList className="w-6 h-6 text-white" />
                            </motion.div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Stock Counts
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {stats.total} sessions ·{' '}
                                    {stats.inProgress} active
                                    {isSingleStore && ' · Warehouse'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw
                                    className={clsx(
                                        'w-4 h-4',
                                        isFetching &&
                                        'animate-spin'
                                    )}
                                />{' '}
                                Refresh
                            </button>
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/counts/bulk'
                                    )
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <BarChart3 className="w-4 h-4" />{' '}
                                Bulk Upload
                            </button>
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() =>
                                    setShowNewCountModal(true)
                                }
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 text-sm"
                                type="button"
                            >
                                <Plus className="w-4 h-4" /> New
                                Count
                            </motion.button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats Dashboard */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <QuickStat
                        label="Total Sessions"
                        value={stats.total}
                        icon={Layers}
                        color="from-slate-500 to-slate-700"
                    />
                    <QuickStat
                        label="In Progress"
                        value={stats.inProgress}
                        icon={Activity}
                        color="from-blue-500 to-indigo-600"
                        pulse={stats.inProgress > 0}
                    />
                    <QuickStat
                        label="Completed"
                        value={stats.completed}
                        icon={CheckCircle}
                        color="from-emerald-500 to-teal-600"
                    />
                    <QuickStat
                        label="Cancelled"
                        value={stats.cancelled}
                        icon={AlertTriangle}
                        color="from-amber-500 to-orange-600"
                    />
                    <QuickStat
                        label="Total Variances"
                        value={stats.totalVariances}
                        icon={TrendingUp}
                        color="from-purple-500 to-indigo-600"
                    />
                    <QuickStat
                        label="Avg Accuracy"
                        value={`${stats.avgAccuracy}%`}
                        icon={BarChart3}
                        color="from-rose-500 to-pink-600"
                    />
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col sm:flex-row gap-2">
                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search counts by branch, type, or notes..."
                                value={searchQuery}
                                onChange={(e) =>
                                    setSearchQuery(
                                        e.target.value
                                    )
                                }
                                className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all text-sm"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() =>
                                        setSearchQuery('')
                                    }
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-0.5">
                            {([
                                {
                                    value: 'all' as CountFilter,
                                    label: 'All',
                                },
                                {
                                    value: 'in_progress' as CountFilter,
                                    label: 'Active',
                                },
                                {
                                    value: 'completed' as CountFilter,
                                    label: 'Completed',
                                },
                                {
                                    value: 'cancelled' as CountFilter,
                                    label: 'Cancelled',
                                },
                            ]).map((f) => (
                                <button
                                    key={f.value}
                                    onClick={() =>
                                        setCountFilter(
                                            f.value
                                        )
                                    }
                                    className={clsx(
                                        'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                        countFilter ===
                                            f.value
                                            ? 'bg-white text-emerald-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={(e) =>
                                setSortBy(
                                    e.target
                                        .value as SortBy
                                )
                            }
                            className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                        >
                            <option value="newest">
                                Newest First
                            </option>
                            <option value="oldest">
                                Oldest First
                            </option>
                            <option value="most_items">
                                Most Items
                            </option>
                        </select>

                        {/* View Toggle */}
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            <button
                                onClick={() =>
                                    setViewMode('card')
                                }
                                className={clsx(
                                    'p-2 rounded-lg transition-all',
                                    viewMode === 'card'
                                        ? 'bg-white shadow-sm text-slate-700'
                                        : 'text-slate-400'
                                )}
                                type="button"
                            >
                                <LayoutGrid className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() =>
                                    setViewMode('table')
                                }
                                className={clsx(
                                    'p-2 rounded-lg transition-all',
                                    viewMode === 'table'
                                        ? 'bg-white shadow-sm text-slate-700'
                                        : 'text-slate-400'
                                )}
                                type="button"
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load counts"
                        message="There was a problem loading your inventory count sessions."
                        onRetry={() => refetch()}
                    />
                ) : filteredCounts.length === 0 ? (
                    <EmptyState
                        icon={
                            <ClipboardList className="w-16 h-16" />
                        }
                        title={
                            searchQuery ||
                                countFilter !== 'all'
                                ? 'No matching counts'
                                : 'No count sessions yet'
                        }
                        description={
                            searchQuery ||
                                countFilter !== 'all'
                                ? 'Try adjusting your filters or search terms.'
                                : 'Start your first inventory count to reconcile physical stock with system records.'
                        }
                        action={
                            !searchQuery &&
                                countFilter === 'all'
                                ? {
                                    label: 'Start First Count',
                                    onClick: () =>
                                        setShowNewCountModal(
                                            true
                                        ),
                                }
                                : undefined
                        }
                    />
                ) : viewMode === 'card' ? (
                    <CountCardView
                        counts={filteredCounts}
                        getStatusBadge={getStatusBadge}
                        formatDate={formatDate}
                        getBranchName={getBranchName}
                        navigate={navigate}
                    />
                ) : (
                    <CountTableView
                        counts={filteredCounts}
                        getStatusBadge={getStatusBadge}
                        formatDate={formatDate}
                        getBranchName={getBranchName}
                        navigate={navigate}
                    />
                )}
            </div>

            {/* New Count Modal */}
            <NewCountModal
                isOpen={showNewCountModal}
                onClose={() => setShowNewCountModal(false)}
                branchesList={branchesList}
                isSingleStore={isSingleStore}
                onCreateCount={handleCreateCount}
                isPending={startCountMutation.isPending}
            />
        </div>
    );
}